import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { changeProps } from "find-and";
import { ProjectContentItemDc } from "@evergis/api";

import { getVisibleLayers } from "../../ducks/sharedLayers/selectors";
import { setVisibleLayers } from "../../ducks/sharedLayers/sharedLayers";
import { changeTempSourceLayerVisibility } from "../../ducks/tempSourceLayers/tempSourceLayers";
import { getTempSourceLayers } from "../../ducks/tempSourceLayers/selectors";
import { useResetFeatureCard } from "../../modules/FeatureCard/hooks/useResetFeatureCard";
import { useProjectLayersUpdate } from "./useProjectLayers";
import { useDashboardLayers } from "./useDashboardLayers";
import { useIsDashboard } from "../location/useIsDashboard";
import { useIsShared } from "../api/useIsShared";

type ContentItemKeys = keyof ProjectContentItemDc;

const useProjectLayerProp = <PropName extends ContentItemKeys>(
  item: ProjectContentItemDc,
  propName: PropName,
): [ProjectContentItemDc[PropName], (state: ProjectContentItemDc[PropName]) => void] => {
  const dispatch = useDispatch();
  const isShared = useIsShared();
  const visibleLayers = useSelector(getVisibleLayers);
  const [layers, updateLayers] = useProjectLayersUpdate();
  const [state, setState] = useState(item?.[propName]);

  const updateState = useCallback(
    (newValue: ProjectContentItemDc[PropName]) => {
      const isPropChanged = state !== newValue;
      const shouldUpdateContent = isPropChanged && !!layers && !isShared;

      if (isPropChanged) {
        setState(() => newValue);
      }

      if (isShared && propName === "isVisible" && item?.name) {
        const index = visibleLayers.indexOf(item.name);
        const newVisibleLayers = [...visibleLayers];

        if (index === -1) {
          newVisibleLayers.push(item.name);
        } else {
          newVisibleLayers.splice(index, 1);
        }

        dispatch(setVisibleLayers(newVisibleLayers));
      }

      if (shouldUpdateContent && item?.name) {
        const newItems = changeProps(layers, { name: item.name }, { [propName]: newValue });

        updateLayers(newItems);
      }
    },
    [state, layers, isShared, propName, item?.name, visibleLayers, dispatch, updateLayers],
  );

  useEffect(() => {
    setState(item?.[propName]);
  }, [item?.[propName]]);

  return [state, updateState];
};

const useProjectLayerPropToggle = (
  item: ProjectContentItemDc,
  propName: KeysByType<ProjectContentItemDc, boolean>,
): [boolean, VoidFunction] => {
  const [propState, setPropState] = useProjectLayerProp(item, propName);

  const toggle = useCallback(() => {
    setPropState(!propState);
  }, [propState, setPropState]);

  return [propState, toggle];
};

export const useProjectLayerLegendExpanded = (item: ProjectContentItemDc): [boolean, VoidFunction] =>
  useProjectLayerPropToggle(item, "isLegendExpanded");

export const useProjectLayerVisibility = (
  item: ProjectContentItemDc,
): [boolean, (e: MouseEvent<HTMLButtonElement>) => void] => {
  const dispatch = useDispatch();
  const tempSourceLayers = useSelector(getTempSourceLayers);
  const isDashboard = useIsDashboard();
  const { layers: dashLayers, setDashboardLayer } = useDashboardLayers();
  const [isVisible, toggleVisible] = useProjectLayerPropToggle(item, "isVisible");
  const toggleVisibility = useResetFeatureCard(toggleVisible, [toggleVisible], item?.name);
  const isTemp = item?.name?.startsWith("temp");

  const visible = useMemo(
    () =>
      isDashboard
        ? dashLayers[item?.name]?.isVisible
        : isTemp
        ? tempSourceLayers.find(({ name }) => name === item?.name)?.isVisible
        : isVisible,
    [isDashboard, isTemp, isVisible, item?.name, dashLayers, tempSourceLayers],
  );

  const toggleVisibilityLayer = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (isDashboard) {
        setDashboardLayer({ name: item?.name, isVisible: !dashLayers[item?.name]?.isVisible });
      } else if (isTemp) {
        dispatch(changeTempSourceLayerVisibility(item?.name));
      } else {
        toggleVisibility();
      }
    },
    [isDashboard, isTemp, setDashboardLayer, item?.name, dashLayers, dispatch, toggleVisibility],
  );

  return [visible, toggleVisibilityLayer];
};
